import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash, CaretDown, Buildings, Truck, Paperclip } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useAccess } from "@/lib/access";
import { Link } from "react-router-dom";
import VendorCombobox from "@/components/VendorCombobox";
import VendorFormDialog from "@/components/VendorFormDialog";
import RecipeCombobox from "@/components/RecipeCombobox";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";

// External PO uploads are filed under the vendor (Vendor → Documents) by type.
const PO_DOC_KINDS = [
  { key: "general", label: "Document" },
  { key: "invoice", label: "Invoice" },
];
const ALLOWED_UPLOAD = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx";

const TABS = [
  { key: "internal", label: "Internal POs", icon: Buildings, testId: "tab-internal-pos" },
  { key: "external", label: "External POs", icon: Truck, testId: "tab-external-pos" },
];

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-amber-50 text-amber-700",
  approved: "bg-sky-50 text-sky-700",
  rejected: "bg-rose-50 text-rose-700",
  ordered: "bg-emerald-50 text-emerald-700",
  in_progress: "bg-violet-50 text-violet-700",
  completed: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  open: "bg-sky-50 text-sky-700",
  partial: "bg-amber-50 text-amber-700",
  fulfilled: "bg-emerald-50 text-emerald-700",
};

const STATUS_LABEL = {
  submitted: "Waiting approval",
  open: "Open",
  partial: "Partly received",
  fulfilled: "Fully received",
  in_progress: "In progress",
};

// Internal PO line progress (worked out by the backend from the linked Requisition / Run / External PO).
const LINE_STATE = {
  pending: { label: "Not started", tone: "bg-slate-100 text-slate-600" },
  requested: { label: "Requisition raised", tone: "bg-amber-50 text-amber-700" },
  in_production: { label: "In production", tone: "bg-violet-50 text-violet-700" },
  done: { label: "Produced", tone: "bg-emerald-50 text-emerald-700" },
  ordered: { label: "Ordered", tone: "bg-sky-50 text-sky-700" },
  received: { label: "Received", tone: "bg-emerald-50 text-emerald-700" },
};
const LINE_REF_PATH = {
  requested: (id) => `/inventory/requisitions/${id}`,
  in_production: (id) => `/inventory/production-runs/${id}`,
  done: (id) => `/inventory/production-runs/${id}`,
};
const KIND_LABEL = { produce: "Produce", purchase: "Purchase" };

const vendorDocUrl = (id) => `${API}/vendor-attachments/${id}/download`;

// Shows the next PO number on the create forms (the real one is assigned on submit).
function useNextCode(kind) {
  const [code, setCode] = useState("");
  useEffect(() => {
    api.get("/inventory/po-next-codes").then(({ data }) => setCode(data?.[kind] || "")).catch(() => {});
  }, [kind]);
  return code;
}
const errMsg = (e, fallback = "Failed") => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function PurchaseOrders() {
  const { user } = useAuth();
  const { roles, can } = useAccess();
  const [tab, setTab] = useState("internal");
  const [internalPOs, setInternalPOs] = useState([]);
  const [externalPOs, setExternalPOs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {kind: "internal"|"external", ...}

  const load = async () => {
    setLoading(true);
    try {
      const [ipo, po, vd] = await Promise.all([
        api.get("/inventory/internal-pos"),
        api.get("/inventory/pos"),
        api.get("/vendors"),
      ]);
      setInternalPOs(ipo.data || []);
      setExternalPOs(po.data || []);
      setVendors(vd.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const perms = {
    writeInternal: can("ipo.create"),
    approveInternal: can("ipo.approve"),
    writeExternal: can("po.create"),
    approveExternal: can("po.approve"),
    startProduction: can("material_request.create"),
    newVendor: can("vendor.edit"),
    // Admin / Inventory Admin may approve their own entries
    isAdmin: roles.includes("admin") || roles.includes("inventory"),
    email: (user?.email || "").toLowerCase(),
  };

  const pending = {
    internal: internalPOs.filter((p) => p.status === "submitted").length,
    external: externalPOs.filter((p) => p.status === "submitted").length,
  };
  const done = () => { setDialog(null); load(); };

  return (
    <div className="space-y-5 p-8" data-testid="purchase-orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">Inventory · Purchasing</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Purchase Orders</h1>
          <p className="text-sm text-slate-500 mt-1">
            Internal POs for requests inside the organisation, External POs for orders placed with vendors.
          </p>
        </div>
        {tab === "internal" && perms.writeInternal && (
          <button
            onClick={() => setDialog({ kind: "internal" })}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="new-internal-po-btn"
          >
            <Plus size={14} weight="bold" /> New Internal PO
          </button>
        )}
        {tab === "external" && perms.writeExternal && (
          <button
            onClick={() => setDialog({ kind: "external" })}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            data-testid="new-external-po-btn"
          >
            <Plus size={14} weight="bold" /> New External PO
          </button>
        )}
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              data-testid={t.testId}
            >
              <Icon size={14} weight={active ? "fill" : "bold"} /> {t.label}
              {pending[t.key] > 0 && (
                <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" title="Waiting approval">{pending[t.key]}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "internal" && (
        <InternalPOTable
          rows={internalPOs}
          loading={loading}
          perms={perms}
          onChanged={load}
          onRaiseExternal={(ipo, lineIdx) => setDialog({ kind: "external", fromInternal: ipo, lineIdx })}
          onStartProduction={(ipo, lineIdx) => setDialog({ kind: "produce", ipo, lineIdx })}
        />
      )}
      {tab === "external" && (
        <ExternalPOTable
          rows={externalPOs}
          loading={loading}
          perms={perms}
          onChanged={load}
        />
      )}

      {dialog?.kind === "internal" && (
        <InternalPODialog onClose={() => setDialog(null)} onDone={done} />
      )}
      {dialog?.kind === "external" && (
        <ExternalPODialog
          vendors={vendors}
          onVendorCreated={perms.newVendor ? (v) => setVendors((vs) => [v, ...vs]) : null}
          fromInternal={dialog.fromInternal}
          lineIdx={dialog.lineIdx}
          shortage={dialog.shortage}
          onClose={() => setDialog(null)}
          onDone={() => { done(); setTab("external"); }}
        />
      )}
      {dialog?.kind === "produce" && (
        <StartProductionDialog
          ipo={dialog.ipo}
          lineIdx={dialog.lineIdx}
          canRaisePO={perms.writeExternal}
          onClose={() => { setDialog(null); load(); }}
          onShortage={(shortage) => { load(); setDialog({ kind: "external", shortage }); }}
        />
      )}
    </div>
  );
}

function StatusChip({ value }) {
  return (
    <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded whitespace-nowrap ${STATUS_TONE[value] || "bg-slate-100 text-slate-600"}`}>
      {STATUS_LABEL[value] || value || "—"}
    </span>
  );
}

function Empty({ children }) {
  return (
    <div className="bg-white border border-dashed border-slate-200 rounded-lg px-6 py-12 text-center text-sm text-slate-500">{children}</div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{children}</th>;
}

function LineList({ lines }) {
  return (
    <table className="w-full max-w-xl text-xs">
      <thead>
        <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
          <th className="text-left py-1 font-medium">Item</th>
          <th className="text-right py-1 font-medium">Qty</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((ln, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="py-1.5 text-slate-800">{ln.item_name || ln.item_id}</td>
            <td className="py-1.5 text-right tabular-nums">{ln.qty} {ln.uom}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function progressSummary(lines = []) {
  const done = lines.filter((l) => ["done", "received"].includes(l.progress?.state)).length;
  return `${done} of ${lines.length} line${lines.length === 1 ? "" : "s"} done`;
}

function lineSummary(lines = []) {
  if (lines.length === 0) return "—";
  const first = `${lines[0].item_name} × ${lines[0].qty} ${lines[0].uom || ""}`.trim();
  return lines.length > 1 ? `${first} +${lines.length - 1} more` : first;
}

function Notes({ text }) {
  if (!text) return null;
  return (
    <p className="text-xs text-slate-600">
      <span className="text-slate-400 uppercase tracking-wider text-[10px] mr-2">Note</span>
      {text}
    </p>
  );
}

// Editable PO number. `value === null` means untouched: it shows the suggested
// next number and lets the server allocate it on submit (safe if two people
// submit at once). Anything typed is sent as-is and checked for duplicates.
function PONoField({ value, onChange, suggested }) {
  return (
    <Field label="PO No">
      <input
        value={value ?? suggested}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Leave blank to auto-generate"
        maxLength={40}
        className={inp}
        data-testid="po-no"
      />
      <span className="block text-[11px] text-slate-400 mt-1">
        {value === null ? "Suggested number — change it if you use your own" : value.trim() ? "Custom number" : "Will be auto-generated"}
      </span>
    </Field>
  );
}

const poCodeForSubmit = (value) => (value === null ? "" : value.trim());

// Approve / reject / cancel for either PO kind. Returns a function (po, action).
function poActions(basePath, onChanged) {
  const act = async (po, action, body) => {
    try {
      await api.post(`${basePath}/${po.id}/${action}`, body);
      const verb = { approve: "approved", reject: "rejected", cancel: "cancelled" }[action];
      toast.success(`${po.code} ${verb}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (po, action) => {
    if (action === "reject") {
      const reason = window.prompt(`Reason for rejecting ${po.code}:`);
      if (reason === null) return;
      if (!reason.trim()) return toast.error("A reason is required to reject.");
      return act(po, "reject", { reason });
    }
    if (action === "cancel" && !window.confirm(`Cancel ${po.code}?`)) return;
    return act(po, action);
  };
}

// Approve / Reject buttons, or a note when the approver is the person who raised it.
function DecisionButtons({ po, canApprove, perms, onAction, testPrefix }) {
  if (po.status !== "submitted" || !canApprove) return null;
  const own = (po.created_by_email || "").toLowerCase() === perms.email;
  if (own && !perms.isAdmin) {
    return (
      <span className="text-[11px] text-slate-400 mr-3" title="You raised this PO, so someone else must approve it.">
        Needs another approver
      </span>
    );
  }
  return (
    <>
      <button onClick={() => onAction(po, "approve")} className="text-xs font-medium text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`${testPrefix}-approve-${po.id}`}>Approve</button>
      <button onClick={() => onAction(po, "reject")} className="text-xs font-medium text-rose-700 hover:text-rose-900 mr-3" data-testid={`${testPrefix}-reject-${po.id}`}>Reject</button>
    </>
  );
}

function DecisionNote({ po }) {
  if (po.approved_at) {
    return <p className="text-xs text-slate-500">Approved by {po.approved_by_name || po.approved_by_email} on {new Date(po.approved_at).toLocaleString()}</p>;
  }
  if (po.status === "rejected") {
    return <p className="text-xs text-rose-700">Rejected by {po.rejected_by_name || po.rejected_by_email}: {po.rejection_reason}</p>;
  }
  return null;
}

// ================= Internal POs =================

// Hidden for now: Internal POs are for our own products and External POs for outside
// vendors/clients, raised separately. Flip to true to fulfil IPO lines from here again.
const IPO_FULFIL_ACTIONS = false;

// Each Internal PO line is produced (Requisition → Production Run) or purchased (External PO).
function IPOLines({ ipo, perms, onRaiseExternal, onStartProduction }) {
  const lines = ipo.lines || [];
  const workable = ["approved", "in_progress"].includes(ipo.status);
  const actionable = IPO_FULFIL_ACTIONS && workable;
  const pendingPurchase = lines
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.kind === "purchase" && l.progress?.state === "pending")
    .map(({ i }) => i);

  return (
    <div className="max-w-3xl">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
            <th className="text-left py-1 font-medium">Item</th>
            <th className="text-right py-1 font-medium">Qty</th>
            <th className="text-left py-1 pl-4 font-medium">Type</th>
            <th className="text-left py-1 font-medium">Progress</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((ln, i) => {
            const st = LINE_STATE[ln.progress?.state] || LINE_STATE.pending;
            const pending = (ln.progress?.state || "pending") === "pending";
            const refPath = LINE_REF_PATH[ln.progress?.state];
            const canProduce = actionable && pending && ln.kind !== "purchase" && perms.startProduction;
            const canPurchase = actionable && pending && ln.kind !== "produce" && perms.writeExternal;
            return (
              <tr key={i} className="border-t border-slate-100" data-testid={`ipo-line-${ipo.id}-${i}`}>
                <td className="py-2 text-slate-800">{ln.item_name}</td>
                <td className="py-2 text-right tabular-nums">{ln.qty} {ln.uom}</td>
                <td className="py-2 pl-4 text-slate-600">{KIND_LABEL[ln.kind] || <span className="text-slate-400">Not set</span>}</td>
                <td className="py-2">
                  {workable || !pending ? (
                    <span className="inline-flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded ${st.tone}`}>{st.label}</span>
                      {ln.progress?.ref && (refPath
                        ? <Link to={refPath(ln.progress.ref_id)} className="font-mono text-emerald-700 hover:underline">{ln.progress.ref}</Link>
                        : <span className="font-mono text-slate-600">{ln.progress.ref}</span>)}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {canProduce && (
                    <button onClick={() => onStartProduction(ipo, i)} className="font-medium text-emerald-700 hover:text-emerald-900 ml-3" data-testid={`ipo-produce-${ipo.id}-${i}`}>
                      Start production →
                    </button>
                  )}
                  {canPurchase && (
                    <button onClick={() => onRaiseExternal(ipo, [i])} className="font-medium text-sky-700 hover:text-sky-900 ml-3" data-testid={`ipo-purchase-${ipo.id}-${i}`}>
                      Raise External PO →
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {actionable && perms.writeExternal && pendingPurchase.length > 1 && (
        <button onClick={() => onRaiseExternal(ipo, pendingPurchase)} className="mt-2 text-xs font-medium text-sky-700 hover:text-sky-900" data-testid={`ipo-purchase-all-${ipo.id}`}>
          Raise one External PO for all {pendingPurchase.length} purchase lines →
        </button>
      )}
    </div>
  );
}

function InternalPOTable({ rows, loading, perms, onChanged, onRaiseExternal, onStartProduction }) {
  const [expanded, setExpanded] = useState(null);

  if (!loading && rows.length === 0) {
    return <Empty>No Internal POs yet. Use &quot;New Internal PO&quot; to raise a request.</Empty>;
  }

  const onAction = poActions("/inventory/internal-pos", onChanged);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["PO No", "Items", "Delivery date", "Requested by", "Status", "", ""].map((h, i) => <Th key={i}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = expanded === r.id;
            return (
              <React.Fragment key={r.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(open ? null : r.id)} data-testid={`ipo-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs font-mono text-slate-700 whitespace-nowrap">{r.code}</td>
                  <td className="px-4 py-3 text-sm text-slate-900">{lineSummary(r.lines)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.delivery_date || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.created_by_name || r.created_by_email}</td>
                  <td className="px-4 py-3">
                    <StatusChip value={r.status} />
                    {["approved", "in_progress", "completed"].includes(r.status) && (
                      <div className="text-[10px] text-slate-500 mt-1">{progressSummary(r.lines)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <DecisionButtons po={r} canApprove={perms.approveInternal} perms={perms} onAction={onAction} testPrefix="ipo" />
                    {IPO_FULFIL_ACTIONS && ["approved", "in_progress"].includes(r.status) && (r.lines || []).some((l) => l.progress?.state === "pending") && (
                      <button onClick={() => setExpanded(r.id)} className="text-xs font-medium text-emerald-700 hover:text-emerald-900 mr-3" data-testid={`ipo-fulfil-${r.id}`}>
                        Fulfil →
                      </button>
                    )}
                    {r.status === "submitted" && perms.writeInternal && (
                      <button onClick={() => onAction(r, "cancel")} className="text-xs text-slate-500 hover:text-rose-700" data-testid={`ipo-cancel-${r.id}`}>Cancel</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <CaretDown size={14} className={`inline text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </td>
                </tr>
                {open && (
                  <tr className="bg-slate-50/70 border-t border-slate-100">
                    <td colSpan={7} className="px-4 py-4 space-y-3">
                      <IPOLines ipo={r} perms={perms} onRaiseExternal={onRaiseExternal} onStartProduction={onStartProduction} />
                      <Notes text={r.notes} />
                      <DecisionNote po={r} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function InternalPODialog({ onClose, onDone }) {
  const nextCode = useNextCode("internal");
  const [poNo, setPoNo] = useState(null);
  const [form, setForm] = useState({ delivery_date: "", notes: "" });
  const [lines, setLines] = useState([{ item_name: "", qty: "", kind: "produce" }]);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const valid = validLines(lines);
    if (!valid) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/inventory/internal-pos", {
        ...form, code: poCodeForSubmit(poNo), lines: valid.map(({ item_name, qty, kind }) => ({ item_name, qty, kind })),
      });
      toast.success(`${data.code} submitted for approval`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New Internal PO" onClose={onClose} onSubmit={submit} testId="internal-po-dialog"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
          <button type="submit" disabled={submitting} className={btnPrimary} data-testid="ipo-submit">
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <PONoField value={poNo} onChange={setPoNo} suggested={nextCode} />
        <Field label="Delivery Date *">
          <input required type="date" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className={inp} data-testid="ipo-delivery-date" />
        </Field>
      </div>
      <LinesEditor lines={lines} setLines={setLines} withKind />
      <p className="text-[11px] text-slate-400 -mt-2">
        <strong>Produce</strong> — made in the plant (goes to a production Requisition → Run). <strong>Purchase</strong> — bought from a vendor (goes to an External PO).
      </p>
      <Field label="Note">
        <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={txt} data-testid="ipo-notes" />
      </Field>
    </Modal>
  );
}

// Returns [{item_name, qty, ...other line fields}] or null (after showing why) if the lines aren't valid.
function validLines(lines) {
  const touched = lines.filter((l) => l.item_name.trim() || l.qty);
  const valid = touched.filter((l) => l.item_name.trim() && Number(l.qty) > 0);
  if (valid.length === 0) {
    toast.error("Add at least one item with a quantity");
    return null;
  }
  if (valid.length !== touched.length) {
    toast.error("Every line needs an item name and a quantity above 0");
    return null;
  }
  return valid.map((l) => ({ ...l, item_name: l.item_name.trim(), qty: Number(l.qty) }));
}

// ================= External POs =================

function ExternalPOTable({ rows, loading, perms, onChanged }) {
  const [expanded, setExpanded] = useState(null);

  if (!loading && rows.length === 0) {
    return <Empty>No External POs yet. Use &quot;New External PO&quot; to order from a vendor.</Empty>;
  }

  const onAction = poActions("/inventory/pos", onChanged);

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["PO No", "Vendor", "Items", "Delivery date", "Docs", "Status", "", ""].map((h, i) => <Th key={i}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = expanded === r.id;
            const cancellable = ["submitted", "open", "partial"].includes(r.status);
            return (
              <React.Fragment key={r.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(open ? null : r.id)} data-testid={`po-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs font-mono text-slate-700 whitespace-nowrap">{r.code}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{r.vendor_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{lineSummary(r.lines)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.expected_delivery || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">
                    {(r.attachments || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1"><Paperclip size={12} /> {r.attachments.length}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3"><StatusChip value={r.status} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <DecisionButtons po={r} canApprove={perms.approveExternal} perms={perms} onAction={onAction} testPrefix="po" />
                    {cancellable && perms.writeExternal && (
                      <button onClick={() => onAction(r, "cancel")} className="text-xs text-slate-500 hover:text-rose-700" data-testid={`po-cancel-${r.id}`}>Cancel</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <CaretDown size={14} className={`inline text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </td>
                </tr>
                {open && (
                  <tr className="bg-slate-50/70 border-t border-slate-100">
                    <td colSpan={8} className="px-4 py-4 space-y-3">
                      <LineList lines={r.lines || []} />
                      <Notes text={r.notes} />
                      {(r.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {r.attachments.map((a) => (
                            <a key={a.id} href={vendorDocUrl(a.id)} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-slate-200 bg-white text-slate-700 hover:border-emerald-400">
                              <Paperclip size={12} />
                              <span className="text-[10px] uppercase tracking-wider text-slate-400">{a.kind === "invoice" ? "Invoice" : "Document"}</span>
                              {a.file_name}
                            </a>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        Raised by {r.created_by_name || r.created_by_email}
                        {r.internal_po_id && " · from an Internal PO"}
                      </p>
                      <DecisionNote po={r} />
                      {r.status === "submitted" && (
                        <p className="text-xs text-amber-700">Goods can be received once this PO is approved.</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// `fromInternal` + `lineIdx`: order those Internal PO lines. `shortage`: buy raw materials a production batch is short of.
function ExternalPODialog({ vendors, onVendorCreated, fromInternal, lineIdx, shortage, onClose, onDone }) {
  const nextCode = useNextCode("external");
  const [poNo, setPoNo] = useState(null);
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "",
    expected_delivery: fromInternal?.delivery_date || shortage?.delivery_date || "",
    notes: fromInternal?.notes || shortage?.notes || "",
  });
  const [lines, setLines] = useState(() => {
    if (fromInternal) {
      // ipo_line ties each prefilled line back to its Internal PO line; removing it here leaves that line unordered.
      return (lineIdx || []).map((i) => ({ item_name: fromInternal.lines[i].item_name || "", qty: String(fromInternal.lines[i].qty), ipo_line: i }));
    }
    if (shortage) return shortage.lines.map((l) => ({ item_name: l.item_name, qty: String(l.qty) }));
    return [{ item_name: "", qty: "" }];
  });
  const [files, setFiles] = useState([]); // [{ key, file, kind: "general" | "invoice" }]
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (list) => {
    const picked = Array.from(list || []).map((file) => ({ key: `${file.name}-${file.size}-${Math.random()}`, file, kind: "general" }));
    setFiles((fs) => [...fs, ...picked]);
  };
  const setKind = (key, kind) => setFiles((fs) => fs.map((f) => (f.key === key ? { ...f, kind } : f)));
  const removeFile = (key) => setFiles((fs) => fs.filter((f) => f.key !== key));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) return toast.error("Select the vendor");
    const valid = validLines(lines);
    if (!valid) return;
    const ipoLines = valid.filter((l) => l.ipo_line != null).map((l) => l.ipo_line);
    if (fromInternal && ipoLines.length === 0) return toast.error(`Keep at least one line from ${fromInternal.code}`);
    setSubmitting(true);
    let po;
    try {
      const { data } = await api.post("/inventory/pos", {
        ...form,
        code: poCodeForSubmit(poNo),
        internal_po_id: fromInternal?.id || "",
        internal_po_lines: ipoLines,
        lines: valid.map(({ item_name, qty }) => ({ item_name, qty })),
      });
      po = data;
    } catch (err) {
      toast.error(errMsg(err));
      setSubmitting(false);
      return;
    }
    // The PO exists now — upload each file under the vendor, tagged with this PO.
    const failed = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f.file);
      try {
        await api.post(`/vendors/${form.vendor_id}/attachments`, fd, {
          params: { kind: f.kind, po_id: po.id },
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch {
        failed.push(f.file.name);
      }
    }
    setSubmitting(false);
    if (failed.length) {
      toast.warning(`${po.code} created, but these files didn't upload: ${failed.join(", ")}. Add them from the vendor's Documents tab.`);
    } else {
      toast.success(`${po.code} submitted for approval${files.length ? ` with ${files.length} file(s)` : ""}`);
    }
    onDone();
  };

  return (
    <>
    <Modal
      title={fromInternal ? `New External PO from ${fromInternal.code}` : shortage ? "New External PO for production shortage" : "New External PO"}
      onClose={onClose}
      onSubmit={submit}
      testId="external-po-dialog"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
          <button type="submit" disabled={submitting} className={btnPrimary} data-testid="po-submit">
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <PONoField value={poNo} onChange={setPoNo} suggested={nextCode} />
        <Field label="Delivery Date *">
          <input required type="date" value={form.expected_delivery} onChange={(e) => set("expected_delivery", e.target.value)} className={inp} data-testid="po-delivery-date" />
        </Field>
      </div>
      <div className="block">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">Vendor *</span>
          {onVendorCreated && <button type="button" onClick={() => setNewVendorOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-emerald-800" data-testid="new-vendor-btn">
            <Plus size={12} weight="bold" /> New vendor
          </button>}
        </div>
        <VendorCombobox vendors={vendors} value={form.vendor_id} onPick={(id) => set("vendor_id", id)} testId="po-vendor" placeholder="Select vendor…" />
      </div>
      <LinesEditor lines={lines} setLines={setLines} />
      <Field label="Note">
        <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={txt} data-testid="po-notes" />
      </Field>

      <div>
        <span className="block text-xs font-medium text-slate-600 mb-1">Upload Document</span>
        <div className="space-y-2">
          {files.map((f) => (
            <div key={f.key} className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm" data-testid="po-file-row">
              <Paperclip size={14} className="text-slate-500 shrink-0" />
              <span className="flex-1 truncate text-slate-700" title={f.file.name}>{f.file.name}</span>
              <div className="flex rounded-md border border-slate-200 overflow-hidden shrink-0">
                {PO_DOC_KINDS.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setKind(f.key, k.key)}
                    className={`px-2.5 h-7 text-xs font-medium ${f.kind === k.key ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`po-file-kind-${k.key}`}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => removeFile(f.key)} className="p-1 text-slate-400 hover:text-rose-600" title="Remove">
                <X size={12} />
              </button>
            </div>
          ))}
          <label className="w-full inline-flex items-center justify-center gap-2 h-10 px-3 rounded-md border border-dashed border-slate-300 bg-white text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
            <Plus size={14} /> Add file
            <input type="file" multiple accept={ALLOWED_UPLOAD} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="po-file-input" />
          </label>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Mark each file as Document or Invoice — it&apos;s saved under the vendor&apos;s Documents / Invoices tab with this PO number.</p>
      </div>
    </Modal>
    {newVendorOpen && (
      <QuickAddVendor
        onClose={() => setNewVendorOpen(false)}
        onCreated={(v) => { onVendorCreated(v); set("vendor_id", v.id); setNewVendorOpen(false); }}
      />
    )}
    </>
  );
}

// ================= Start production from an Internal PO line =================

const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

// Raises a production Requisition for the line (Store approves it → Production Run).
// If raw materials are short, offers an External PO for just the shortfall.
function StartProductionDialog({ ipo, lineIdx, canRaisePO, onClose, onShortage }) {
  const line = ipo.lines[lineIdx];
  const [recipes, setRecipes] = useState([]);
  const [fgItems, setFgItems] = useState([]);
  const [form, setForm] = useState({ recipe_id: "", batch_size: String(line.qty), fg_item_id: "", target_production_date: ipo.delivery_date || "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.all([api.get("/inventory/recipes"), api.get("/inventory/items", { params: { category: "finished_goods" } })])
      .then(([r, it]) => {
        setRecipes(r.data || []);
        setFgItems(it.data || []);
        // Pre-pick the recipe / finished-goods item named like the line, e.g. "Chicken Biryani".
        setForm((f) => ({
          ...f,
          recipe_id: (r.data || []).find((x) => sameName(x.name, line.item_name))?.id || "",
          fg_item_id: (it.data || []).find((x) => sameName(x.name, line.item_name))?.id || "",
        }));
      })
      .catch((e) => toast.error(errMsg(e, "Failed to load recipes")));
  }, [line.item_name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipe_id) return toast.error("Select the recipe");
    if (!(Number(form.batch_size) > 0)) return toast.error("Batch size must be above 0");
    setSubmitting(true);
    try {
      const { data } = await api.post("/inventory/requisitions", {
        ...form,
        batch_size: Number(form.batch_size),
        notes: [form.notes.trim(), `For ${ipo.code}`].filter(Boolean).join(" — "),
        submit: true,
        internal_po_id: ipo.id,
        internal_po_line: lineIdx,
      });
      if (data.total_shortage_at_request > 0) {
        setCreated(data);
      } else {
        toast.success(`${data.code} sent to Store for approval — the Production Run is created when they approve`);
        onClose();
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const short = (created?.materials || []).filter((m) => m.shortage_at_request > 0);

  if (created) {
    return (
      <Modal title={`${created.code} raised — materials short`} subtitle={`${line.item_name} × ${created.batch_size} kg for ${ipo.code}`} onClose={onClose} testId="produce-shortage"
        footer={
          <>
            <button type="button" onClick={onClose} className={btnSecondary}>Close</button>
            {canRaisePO && (
              <button
                type="button"
                className={btnPrimary}
                onClick={() => onShortage({
                  lines: short.map((m) => ({ item_name: m.ingredient_name || m.item_code || "Material", qty: m.shortage_at_request })),
                  delivery_date: ipo.delivery_date,
                  notes: `Shortage for ${created.code} (${ipo.code} — ${line.item_name})`,
                })}
                data-testid="produce-raise-shortage-po"
              >
                Raise External PO for shortage →
              </button>
            )}
          </>
        }
      >
        <p className="text-sm text-slate-600">
          The requisition is with Store, but they can&apos;t approve it until these materials are in stock:
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-400 uppercase tracking-wider text-[10px]">
              <th className="text-left py-1 font-medium">Material</th>
              <th className="text-right py-1 font-medium">Needed</th>
              <th className="text-right py-1 font-medium">In stock</th>
              <th className="text-right py-1 font-medium">Short</th>
            </tr>
          </thead>
          <tbody>
            {short.map((m, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="py-1.5 text-slate-800">{m.ingredient_name || m.item_code}</td>
                <td className="py-1.5 text-right tabular-nums">{m.required} {m.uom}</td>
                <td className="py-1.5 text-right tabular-nums">{m.available_at_request} {m.uom}</td>
                <td className="py-1.5 text-right tabular-nums font-medium text-rose-700">{m.shortage_at_request} {m.uom}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    );
  }

  return (
    <Modal title={`Start production — ${line.item_name}`} subtitle={`${ipo.code} · ${line.qty} kg · deliver by ${ipo.delivery_date || "—"}`} onClose={onClose} onSubmit={submit} testId="produce-dialog"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
          <button type="submit" disabled={submitting} className={btnPrimary} data-testid="produce-submit">
            {submitting ? "Sending…" : "Send to Store"}
          </button>
        </>
      }
    >
      <Field label="Recipe *">
        <RecipeCombobox recipes={recipes} value={form.recipe_id} onPick={(v) => set("recipe_id", v)} testId="produce-recipe" placeholder="Select recipe…" className="h-10" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Batch size (kg) *">
          <input type="number" step="0.001" min="0" value={form.batch_size} onChange={(e) => set("batch_size", e.target.value)} className={inp} data-testid="produce-batch-size" />
        </Field>
        <Field label="Target production date">
          <input type="date" value={form.target_production_date} onChange={(e) => set("target_production_date", e.target.value)} className={inp} />
        </Field>
      </div>
      <Field label="Finished-goods item (where the output is stocked)">
        <InventoryItemCombobox items={fgItems} value={form.fg_item_id} onPick={(v) => set("fg_item_id", v)} testId="produce-fg" placeholder="— Don't track FG stock —" className="h-10" showAvailability={false} />
      </Field>
      <Field label="Note">
        <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={txt} />
      </Field>
      <p className="text-[11px] text-slate-400">
        This raises a production Requisition. Store checks stock and approves it, which creates the Production Run.
      </p>
    </Modal>
  );
}

// ================= Shared bits =================

// Quick-add from the External PO form: creates the vendor and hands it back so the
// form can select it straight away.
function QuickAddVendor({ onClose, onCreated }) {
  const submit = async (payload) => {
    try {
      const { data } = await api.post("/vendors", payload);
      toast.success(`Vendor "${data.name}" created`);
      onCreated(data);
    } catch (e) {
      toast.error(errMsg(e, "Failed to create vendor"));
    }
  };
  return <VendorFormDialog open onClose={onClose} onSubmit={submit} />;
}

// PO lines are a free-text name + quantity, always in kg. `withKind` adds a Produce / Purchase choice (Internal POs).
function LinesEditor({ lines, setLines, withKind = false }) {
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const add = () => setLines((ls) => [...ls, withKind ? { item_name: "", qty: "", kind: "produce" } : { item_name: "", qty: "" }]);
  const remove = (i) => setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, j) => j !== i)));

  return (
    <div>
      <span className="block text-xs font-medium text-slate-600 mb-1">Items *</span>
      <div className="space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`line-${i}`}>
            <div className="flex-1 min-w-0">
              <input value={l.item_name} onChange={(e) => setLine(i, "item_name", e.target.value)} placeholder="Item name, e.g. Chicken Biryani" className={inp} data-testid={`line-item-${i}`} />
            </div>
            <div className="relative">
              <input type="number" step="0.001" min="0" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className={`${inp} w-28 pr-9`} data-testid={`line-qty-${i}`} />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">kg</span>
            </div>
            {withKind && (
              <div className="flex rounded-md border border-slate-200 overflow-hidden shrink-0" data-testid={`line-kind-${i}`}>
                {Object.entries(KIND_LABEL).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setLine(i, "kind", k)}
                    className={`px-2.5 h-10 text-xs font-medium ${l.kind === k ? (k === "produce" ? "bg-emerald-600 text-white" : "bg-sky-600 text-white") : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`line-kind-${i}-${k}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button type="button" onClick={() => remove(i)} disabled={lines.length === 1} className="p-2 text-slate-400 hover:text-rose-600 disabled:opacity-30" title="Remove line">
              <Trash size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4">
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900" data-testid="add-line">
          <Plus size={12} weight="bold" /> Add item
        </button>
      </div>
    </div>
  );
}

function Modal({ title, subtitle, onClose, onSubmit, footer, children, testId, wide = false }) {
  const Wrapper = onSubmit ? "form" : "div";
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid={testId}>
      <Wrapper onSubmit={onSubmit} className={`bg-white rounded-lg shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-2xl"}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">{children}</div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">{footer}</div>
      </Wrapper>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
const txt = "w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";
const btnPrimary = "h-10 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50";
const btnSecondary = "h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50";
